import type { Attribution } from '@zetter/shared';

/**
 * Откуда пришёл человек: метки кампании собираются при первом заходе.
 *
 * Между «увидел объявление» и «завёл аккаунт» человек успевает уйти, вернуться,
 * почитать оферту и зайти с закладки. К моменту регистрации адресная строка про
 * рекламу уже ничего не помнит — поэтому метки снимаются на входе и лежат до
 * регистрации.
 *
 * Хранится ПЕРВОЕ касание, а не последнее, и не перезаписывается. Пришёл по
 * объявлению, ушёл думать, вернулся через неделю по прямой ссылке — регистрацию
 * всё равно привела реклама, и запись об этом должна пережить второй заход.
 *
 * localStorage, а не cookie: метки нужны только браузеру, на сервер они уходят
 * один раз — в теле регистрации. Cookie каталась бы с каждым запросом впустую.
 */
const KEY = 'zetter.attribution';

/**
 * Идентификатор браузера в Метрике — в своём ключе, а не внутри записи выше.
 *
 * Две причины. Запись о первом касании не заводится вовсе у тех, кто пришёл
 * прямо, — а идентификатор нужен для всех: офлайн-конверсию по оплате счёта
 * придётся отдавать и за них. И живут они разное время: метки после
 * регистрации стираются, отработав, а браузер остаётся тем же браузером.
 */
const CLIENT_ID_KEY = 'zetter.metrika-cid';

/**
 * Сколько метка считается действующей.
 *
 * Тридцать дней — обычное окно атрибуции у рекламных площадок. Дальше связь
 * между объявлением и регистрацией перестаёт быть правдой: за месяц человек
 * успевает узнать о продукте ещё пятью способами.
 */
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

const FIELDS: Record<string, keyof Attribution> = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_term: 'utmTerm',
  utm_content: 'utmContent',
  yclid: 'yclid',
  gclid: 'gclid',
};

interface Stored extends Attribution {
  /** Метка времени записи: по ней запись протухает. */
  savedAt: number;
}

/**
 * Снять метки с адреса и запомнить, если ещё не помним.
 *
 * Зовётся на каждой публичной странице и на входе в кабинет: человек вполне
 * может прийти по объявлению сразу на форму регистрации.
 */
export function captureAttribution(): void {
  if (typeof window === 'undefined') return;

  const stored = read();
  // Первое касание уже записано и не протухло — второй заход его не трогает.
  if (stored) return;

  const params = new URLSearchParams(window.location.search);
  const captured: Attribution = {};
  for (const [param, field] of Object.entries(FIELDS)) {
    const value = params.get(param)?.trim();
    if (value) captured[field] = value.slice(0, 200);
  }

  const referrerHost = externalReferrerHost();

  /*
   * Заход без единой метки и без чужого перехода не записывается вовсе: это
   * прямой заход, и пустая запись только мешала бы — она заняла бы место
   * первого касания, и настоящая метка со следующего перехода уже не легла бы.
   */
  if (Object.keys(captured).length === 0 && !referrerHost) return;

  if (referrerHost) captured.referrerHost = referrerHost;
  captured.landingPath = window.location.pathname.slice(0, 500);
  captured.firstSeenAt = new Date().toISOString();

  write({ ...captured, savedAt: Date.now() });
}

/**
 * Запомнить идентификатор браузера. Зовёт Метрика, когда счётчик ответит.
 *
 * Значение обрезается по той же границе, что и метки: сюда оно приходит от
 * чужого скрипта, и контракт на сервере длиннее двухсот символов не примет.
 */
export function rememberClientId(clientId: string): void {
  try {
    window.localStorage.setItem(CLIENT_ID_KEY, clientId.slice(0, 200));
  } catch {
    // Приватный режим и запрет хранилища — не повод ронять страницу.
  }
}

/**
 * Что отправить вместе с регистрацией.
 *
 * Пусто — ни меток, ни счётчика: человек пришёл сам, и Метрика на странице не
 * работает. У всех остальных здесь как минимум идентификатор браузера, даже
 * если кампании за ними не стоит никакой.
 */
export function readAttribution(): Attribution | undefined {
  const attribution: Attribution = {};

  const stored = read();
  if (stored) {
    const { savedAt: _savedAt, ...marks } = stored;
    Object.assign(attribution, marks);
  }

  const clientId = readClientId();
  if (clientId) attribution.metrikaClientId = clientId;

  return Object.keys(attribution).length > 0 ? attribution : undefined;
}

/**
 * Забыть метки.
 *
 * Зовётся после успешной регистрации: своё дело они сделали, а оставшись,
 * приписали бы той же кампании второй аккаунт, заведённый с того же браузера.
 *
 * Идентификатор браузера при этом остаётся: он не про кампанию, а про то, куда
 * Метрике класть события, которые придут позже, — а приходят они как раз после
 * регистрации.
 */
export function clearAttribution(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // Приватный режим и запрет хранилища — не повод ронять страницу.
  }
}

function readClientId(): string | undefined {
  try {
    return window.localStorage.getItem(CLIENT_ID_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function read(): Stored | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Stored;
    if (typeof parsed?.savedAt !== 'number') return null;
    if (Date.now() - parsed.savedAt > TTL_MS) {
      window.localStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function write(value: Stored): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    // Хранилище может быть переполнено или запрещено. Атрибуция — не та вещь,
    // ради которой стоит показывать человеку ошибку.
  }
}

/**
 * Домен, с которого пришли, — если это не мы сами.
 *
 * Только домен, а не полный адрес: полный несёт чужие параметры запроса, а
 * иногда и чужие персональные данные, которые нам не нужны и хранить которые
 * мы не подписывались.
 */
function externalReferrerHost(): string | undefined {
  const referrer = document.referrer;
  if (!referrer) return undefined;

  try {
    const host = new URL(referrer).hostname;
    return host && host !== window.location.hostname ? host.slice(0, 200) : undefined;
  } catch {
    return undefined;
  }
}
